/**
 * Workflow action panel for ScheduleDetailModal.
 *
 * Renders the action buttons (Accept, Decline, Propose Date, Propose Qty,
 * Hold) and dispatches the click to the same pop-up modals used by the
 * per-row pencil/check/X icons in ScheduleLinesTable. Mounting those modals
 * here (rather than inline forms) means:
 *   - the reason-code dropdown is consistent with every other workflow action,
 *   - the action UI is visible (modal pops up over the detail dialog) instead
 *     of rendering below the detail dialog's fold where the user can't see it,
 *   - the codepath is identical to the per-row flow, so a single backend
 *     contract change ripples through one site rather than two.
 */
import { useState } from 'react';
import { Check, XCircle, CalendarDays, Hash, PauseCircle } from 'lucide-react';
import { Button } from '../ui/button';
import type { POLineSchedule } from '../../lib/api/types/purchasing';
import {
  AcceptScheduleModal,
  DeclineScheduleModal,
  ProposeScheduleDateModal,
  ProposeScheduleQtyModal,
  HoldScheduleModal,
  ReleaseScheduleModal,
} from './tabs/schedule-lines/ScheduleModals';
import type { ScheduleActionPayload } from './tabs/schedule-lines/ScheduleModals';

type ActionKind =
  | 'accept'
  | 'decline'
  | 'propose_date'
  | 'propose_qty'
  | 'hold'
  | 'release'
  | null;

interface ScheduleDetailActionsProps {
  schedule: POLineSchedule;
  /**
   * Legacy prop kept for backward compatibility with the parent modal — the
   * parent still owns the "hold" trigger from its header pause button. When
   * the parent sets activeAction='hold' we open the hold pop-up modal.
   */
  activeAction: string | null;
  setActiveAction: (action: string | null) => void;
  canShowWorkflowActions: boolean;
  canShowProposeChange: boolean;
  onWorkflowAction: (action: string, data: Record<string, unknown>) => Promise<void>;
  onToggleHold: (reason?: string) => Promise<void>;
}

export default function ScheduleDetailActions({
  schedule,
  activeAction,
  setActiveAction,
  canShowWorkflowActions,
  canShowProposeChange,
  onWorkflowAction,
  onToggleHold,
}: ScheduleDetailActionsProps) {
  const [openModal, setOpenModal] = useState<ActionKind>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bridge: the parent modal sets activeAction='hold' when its header
  // PauseCircle button is clicked. Translate that into the pop-up modal.
  const effectiveModal: ActionKind =
    openModal ?? (activeAction === 'hold' ? 'hold' : null);

  const closeModal = () => {
    setOpenModal(null);
    if (activeAction) setActiveAction(null);
  };

  const showAnyAction = canShowWorkflowActions || canShowProposeChange;
  // CR-7739: the schedule's site has quantity changes switched off. No
  // Propose Qty for either side, and a staged quantity is not accepted (the
  // server refuses it; Decline still clears it).
  const quantityHidden = !!schedule.quantity_proposals_hidden;
  const acceptBlocked = quantityHidden && schedule.supplier_proposed_qty != null;
  const canHold = !schedule.on_hold;
  const renderAnything = showAnyAction || canHold || effectiveModal !== null;
  if (!renderAnything) return null;

  const runWorkflow = async (
    action: 'accept' | 'decline' | 'propose_date' | 'propose_qty',
    data: Record<string, unknown>,
  ) => {
    setSubmitting(true);
    try {
      await onWorkflowAction(action, data);
      setOpenModal(null);
    } catch {
      // Toast already fired by parent — keep modal open so user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async (data: ScheduleActionPayload) => {
    await runWorkflow('accept', { comments: data.comments });
  };

  const handleDecline = async (data: ScheduleActionPayload) => {
    await runWorkflow('decline', {
      reason: data.reason,
      comments: data.comments,
    });
  };

  const handleProposeDate = async (data: ScheduleActionPayload) => {
    await runWorkflow('propose_date', {
      proposed_date: data.proposedDate,
      reason: data.reason,
      comments: data.comments,
    });
  };

  const handleProposeQty = async (data: ScheduleActionPayload) => {
    await runWorkflow('propose_qty', {
      proposed_quantity: data.proposedQty,
      reason: data.reason,
      comments: data.comments,
    });
  };

  const handleHold = async (data: ScheduleActionPayload) => {
    setSubmitting(true);
    try {
      await onToggleHold(data.reason);
      closeModal();
    } catch {
      // Parent already toasted.
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async () => {
    setSubmitting(true);
    try {
      await onToggleHold();
      closeModal();
    } catch {
      // Parent already toasted.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      {(showAnyAction || canHold) && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Actions
          </h3>
          <div className="flex flex-wrap gap-2">
            {canShowWorkflowActions && (
              <>
                {!acceptBlocked && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setOpenModal('accept')}
                  type="button"
                >
                  <Check className="h-4 w-4 mr-1" /> Accept
                </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setOpenModal('decline')}
                  type="button"
                >
                  <XCircle className="h-4 w-4 mr-1" /> Decline
                </Button>
              </>
            )}
            {canShowProposeChange && (
              <>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setOpenModal('propose_date')}
                  type="button"
                >
                  <CalendarDays className="h-4 w-4 mr-1" /> Propose Date
                </Button>
                {!quantityHidden && (
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => setOpenModal('propose_qty')}
                  type="button"
                >
                  <Hash className="h-4 w-4 mr-1" /> Propose Qty
                </Button>
                )}
              </>
            )}
            {canHold && (
              <Button
                size="sm"
                variant="outline"
                className="border-orange-500 text-orange-700 hover:bg-orange-50"
                onClick={() => setOpenModal('hold')}
                type="button"
              >
                <PauseCircle className="h-4 w-4 mr-1" /> Put On Hold
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Pop-up modals — same components used by per-row actions. */}
      <AcceptScheduleModal
        isOpen={effectiveModal === 'accept'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleAccept}
      />
      <DeclineScheduleModal
        isOpen={effectiveModal === 'decline'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleDecline}
      />
      <ProposeScheduleDateModal
        isOpen={effectiveModal === 'propose_date'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleProposeDate}
      />
      <ProposeScheduleQtyModal
        isOpen={effectiveModal === 'propose_qty'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleProposeQty}
      />
      <HoldScheduleModal
        isOpen={effectiveModal === 'hold'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleHold}
      />
      <ReleaseScheduleModal
        isOpen={effectiveModal === 'release'}
        schedule={schedule}
        onClose={closeModal}
        submitting={submitting}
        onSubmit={handleRelease}
      />
    </div>
  );
}
